import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, RotateCcw, Search, ChevronDown, FileText } from "lucide-react";
import { HKMonogram } from "@/components/BrandMark";
import usePageTitle from "@/hooks/usePageTitle";
import { usePageContentContext } from "@/context/PageContentContext";
import AdminLayout from "@/components/AdminLayout";
import { ImageField } from "@/components/MediaUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "hk_admin_pw";

/**
 * The content schema: every editable string on the storefront is
 * declared here. Extend this list at any time — the schema is the
 * single source of truth for what the admin can edit.
 *
 * `kind` = "text" (short one-liner) · "textarea" (multi-line) · "url" (image URL) · "html" (raw HTML block)
 */
const SCHEMA = [
  {
    page: "home",
    label: "Homepage",
    sections: [
      { section: "announcement.text",  label: "Announcement bar text",   kind: "text",  fallback: "" },
      { section: "hero.eyebrow",       label: "Hero eyebrow tag",         kind: "text",  fallback: "A Brand Rooted in Tradition" },
      { section: "hero.title.line1",   label: "Hero title — line 1",     kind: "text",  fallback: "Ayurveda for" },
      { section: "hero.title.line2",   label: "Hero title — line 2 (italic accent)", kind: "text", fallback: "modern living." },
      { section: "hero.paragraph",     label: "Hero paragraph",          kind: "textarea", fallback: "" },
      { section: "hero.cta.primary",   label: "Hero primary CTA label",  kind: "text",  fallback: "Explore Our Collection" },
      { section: "hero.cta.secondary", label: "Hero secondary CTA label",kind: "text",  fallback: "Read Our Story" },
      { section: "featured.eyebrow",   label: "Featured section — eyebrow", kind: "text", fallback: "Our Flagship" },
      { section: "featured.title",     label: "Featured section — title", kind: "text",  fallback: "Sea Buckthorn Pure Pulp" },
      { section: "botanicals.eyebrow", label: "Botanicals — eyebrow",     kind: "text",  fallback: "Rooted in the Earth" },
      { section: "botanicals.title",   label: "Botanicals — title",       kind: "text",  fallback: "The Botanicals That Inspire Our Craft" },
      { section: "botanicals.paragraph", label: "Botanicals — paragraph", kind: "textarea", fallback: "" },
      { section: "founder.eyebrow",    label: "Founder story — eyebrow",  kind: "text",  fallback: "The Founder's Note" },
      { section: "founder.title",      label: "Founder story — title",    kind: "text",  fallback: "A Journey Home" },
      { section: "founder.paragraph",  label: "Founder story — paragraph",kind: "textarea", fallback: "" },
      { section: "why.eyebrow",        label: "Why HerbKranti — eyebrow", kind: "text",  fallback: "Why HerbKranti" },
      { section: "why.title",          label: "Why HerbKranti — title",   kind: "text",  fallback: "A brand built on quiet conviction." },
      { section: "reviews.title",      label: "Reviews — title",           kind: "text",  fallback: "What Our Community Says" },
    ],
  },
  {
    page: "our-story",
    label: "Our Story",
    sections: [
      { section: "act1.eyebrow",  label: "Act I eyebrow",      kind: "text",     fallback: "An Honest Beginning" },
      { section: "act1.line1",    label: "Act I — line 1",     kind: "text",     fallback: "I was not building" },
      { section: "act1.line2",    label: "Act I — line 2",     kind: "text",     fallback: "a brand." },
      { section: "act1.line3",    label: "Act I — line 3",     kind: "text",     fallback: "I was rediscovering something." },
      { section: "signature.name",label: "Founder signature — name", kind: "text", fallback: "— Pankaj D. Agarwal" },
      { section: "signature.role",label: "Founder signature — role", kind: "text", fallback: "Founder & Partner" },
      { section: "signature.entity",label: "Founder signature — entity", kind: "text", fallback: "HerbKranti Wellness LLP" },
    ],
  },
  {
    page: "products",
    label: "Products Listing",
    sections: [
      { section: "hero.eyebrow",   label: "Hero eyebrow",     kind: "text",     fallback: "The Collection" },
      { section: "hero.title",     label: "Hero title",       kind: "text",     fallback: "Timeless Rituals, Modern Rediscovered" },
      { section: "hero.paragraph", label: "Hero paragraph",   kind: "textarea", fallback: "" },
    ],
  },
  {
    page: "reviews",
    label: "Reviews Page",
    sections: [
      { section: "hero.title",     label: "Hero title",       kind: "text",     fallback: "In Their Words" },
      { section: "hero.paragraph", label: "Hero paragraph",   kind: "textarea", fallback: "" },
    ],
  },
  {
    page: "faq",
    label: "FAQ Page (page copy)",
    sections: [
      { section: "hero.title",     label: "Hero title",       kind: "text",     fallback: "Questions, Answered" },
      { section: "hero.paragraph", label: "Hero paragraph",   kind: "textarea", fallback: "" },
    ],
  },
  {
    page: "contact",
    label: "Contact",
    sections: [
      { section: "phone",    label: "Phone number",  kind: "text",     fallback: "+91 72497 97972" },
      { section: "email",    label: "Email address", kind: "text",     fallback: "herbkranti@gmail.com" },
      { section: "whatsapp", label: "WhatsApp URL",  kind: "text",     fallback: "https://wa.me/917249797972" },
      { section: "address",  label: "Office address (multiline)", kind: "textarea", fallback: "" },
    ],
  },
  {
    page: "footer",
    label: "Footer",
    sections: [
      { section: "tagline",       label: "Footer tagline",         kind: "textarea", fallback: "A premium Indian wellness brand — thoughtfully selected, beautifully crafted, and inspired by the botanical heritage of Ayurveda." },
      { section: "copyright.name",label: "Copyright entity",       kind: "text",     fallback: "HerbKranti Wellness LLP" },
    ],
  },
  {
    page: "seo",
    label: "SEO · Meta",
    sections: [
      { section: "meta.title",       label: "Site meta title",       kind: "text",     fallback: "HerbKranti Wellness | Premium Ayurvedic Wellness" },
      { section: "meta.description", label: "Site meta description", kind: "textarea", fallback: "Premium Ayurvedic wellness products crafted with authentic herbs, backed by modern science. Discover HerbKranti." },
      { section: "og.image",         label: "Open-graph image URL",  kind: "url",      fallback: "/og-cover.svg" },
    ],
  },
];

const AdminContentPage = () => {
  usePageTitle("Admin · Content");
  const pw = sessionStorage.getItem(STORAGE_KEY) || "";
  const { refreshPage } = usePageContentContext();

  const [all, setAll] = useState({});             // { page: { section: {value,updated_at} } }
  const [drafts, setDrafts] = useState({});       // { "page::section": "new value" }
  const [openPage, setOpenPage] = useState(SCHEMA[0].page);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/content`, { headers: { "X-Admin-Password": pw } });
      setAll(data || {});
    } catch {
      toast.error("Could not load content. Please sign in via /admin first.");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (pw) load(); }, [pw]);

  const filteredSchema = useMemo(() => {
    if (!query.trim()) return SCHEMA;
    const q = query.toLowerCase();
    return SCHEMA.map((p) => ({
      ...p,
      sections: p.sections.filter((s) => s.label.toLowerCase().includes(q) || s.section.toLowerCase().includes(q)),
    })).filter((p) => p.sections.length > 0);
  }, [query]);

  if (!pw) return <Navigate to="/admin" replace />;

  const currentValue = (pageKey, section, fallback) => {
    const draftKey = `${pageKey}::${section}`;
    if (draftKey in drafts) return drafts[draftKey];
    return all?.[pageKey]?.[section]?.value ?? "";
  };

  const isOverridden = (pageKey, section) => !!all?.[pageKey]?.[section];

  const setDraft = (pageKey, section, value) => {
    setDrafts((d) => ({ ...d, [`${pageKey}::${section}`]: value }));
  };

  const saveOne = async (pageKey, section) => {
    const draftKey = `${pageKey}::${section}`;
    const value = drafts[draftKey] ?? "";
    setBusy(true);
    try {
      await axios.put(`${API}/admin/content/${pageKey}/${section}`, { value }, { headers: { "X-Admin-Password": pw } });
      toast.success("Saved");
      setDrafts((d) => { const { [draftKey]: _, ...rest } = d; return rest; });
      await load();
      await refreshPage(pageKey);
    } catch {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  const resetOne = async (pageKey, section) => {
    if (!window.confirm("Reset this field to its hardcoded default?")) return;
    try {
      await axios.delete(`${API}/admin/content/${pageKey}/${section}`, { headers: { "X-Admin-Password": pw } });
      toast.success("Reset to default");
      await load();
      await refreshPage(pageKey);
    } catch { toast.error("Reset failed"); }
  };

  return (
    <AdminLayout title="Pages & Content" subtitle="Override any hardcoded string across the site">
      <div className="space-y-6" data-testid="admin-content-page">
        <div className="hk-card p-6 flex items-start gap-4 border-[var(--hk-gold)]/40">
          <div className="w-10 h-10 rounded-full bg-[var(--hk-warm-ivory)] border border-[var(--hk-gold)]/40 flex items-center justify-center text-[var(--hk-orange-deep)] shrink-0">
            <FileText size={17} strokeWidth={1.6} />
          </div>
          <div>
            <div className="serif text-[18px] text-[var(--hk-ink)] leading-tight" style={{ paddingBottom: "0.05em" }}>
              Universal content editor
            </div>
            <div className="mt-1 text-[13px] text-[var(--hk-ink-2)] leading-[1.7]">
              Every editable string on the storefront is listed below by page. Save any field to override the hardcoded default. Reset to remove the override and fall back to the built-in copy.
            </div>
          </div>
        </div>

        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--hk-ink-3)]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search fields — try 'hero' or 'contact.email'"
            className="hk-input pl-10 !py-3"
            data-testid="admin-content-search"
          />
        </div>

        <div className="space-y-4">
          {filteredSchema.map((p) => {
            const isOpen = openPage === p.page || !!query.trim();
            return (
              <div key={p.page} className="hk-card p-0 overflow-hidden" data-testid={`admin-content-page-${p.page}`}>
                <button
                  onClick={() => setOpenPage(openPage === p.page ? null : p.page)}
                  className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left hover:bg-[var(--hk-warm-ivory)]/40 transition-colors"
                >
                  <div>
                    <div className="serif text-[20px] text-[var(--hk-ink)] leading-tight" style={{ paddingBottom: "0.05em" }}>
                      {p.label}
                    </div>
                    <div className="mt-1 text-[12px] text-[var(--hk-ink-3)]">
                      {p.sections.length} editable field{p.sections.length > 1 ? "s" : ""}
                    </div>
                  </div>
                  <ChevronDown size={16} className={"transition-transform " + (isOpen ? "rotate-180" : "")} />
                </button>

                {isOpen && (
                  <div className="border-t border-[var(--hk-border)] p-5 md:p-6 space-y-4">
                    {p.sections.map((s) => {
                      const val = currentValue(p.page, s.section, s.fallback);
                      const draftKey = `${p.page}::${s.section}`;
                      const dirty = draftKey in drafts;
                      const overridden = isOverridden(p.page, s.section);
                      return (
                        <div key={s.section} className="p-4 rounded-lg border border-[var(--hk-border)] bg-white" data-testid={`admin-content-field-${p.page}-${s.section}`}>
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div>
                              <div className="text-[13.5px] text-[var(--hk-ink)] font-medium">{s.label}</div>
                              <div className="text-[10.5px] tracking-[0.16em] uppercase text-[var(--hk-ink-3)] mt-0.5">
                                {p.page}.{s.section}
                                {overridden && <span className="ml-2 text-[var(--hk-orange-deep)]">· overridden</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {overridden && (
                                <button
                                  onClick={() => resetOne(p.page, s.section)}
                                  className="text-[11px] tracking-[0.14em] uppercase text-[var(--hk-ink-3)] hover:text-red-600 flex items-center gap-1"
                                  data-testid={`admin-content-reset-${p.page}-${s.section}`}
                                >
                                  <RotateCcw size={12} /> Reset
                                </button>
                              )}
                              <button
                                onClick={() => saveOne(p.page, s.section)}
                                disabled={!dirty || busy}
                                className="hk-btn-primary !py-1.5 !px-3 text-[11px] tracking-[0.14em] disabled:opacity-40"
                                data-testid={`admin-content-save-${p.page}-${s.section}`}
                              >
                                <Save size={12} /> Save
                              </button>
                            </div>
                          </div>
                          {s.kind === "url" ? (
                            <ImageField
                              value={val}
                              onChange={(v) => setDraft(p.page, s.section, v)}
                              label=""
                              aspect="wide"
                              testid={`admin-content-${p.page}-${s.section}`}
                            />
                          ) : s.kind === "textarea" ? (
                            <textarea
                              className="hk-input min-h-[80px]"
                              value={val}
                              onChange={(e) => setDraft(p.page, s.section, e.target.value)}
                              placeholder={s.fallback || "Enter value…"}
                            />
                          ) : (
                            <input
                              className="hk-input"
                              value={val}
                              onChange={(e) => setDraft(p.page, s.section, e.target.value)}
                              placeholder={s.fallback || "Enter value…"}
                            />
                          )}
                          {s.fallback && !overridden && !dirty && (
                            <div className="mt-1.5 text-[11px] text-[var(--hk-ink-3)] italic">Currently using default: "{s.fallback}"</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminContentPage;
